import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

import { InternalButton } from "@sharedComponents/buttons";
import Multimedia from "@sharedComponents/multimedia";

import WeglotNoTranslationWrapper from "@stadium/services/weglot/no-translation-wrapper";
import { isCustomCurrency } from "@stadium/helpers/currency";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";
import { useStoreContext } from "@components/store/provider";

import { getCartOrderCurrency } from "@store/selectors/cart";

import { getTaxons } from "@selectors/config";
import {
  getIsPricelessShop,
  getStoreCurrencyPrefix,
  getStoreCurrencySuffix,
} from "@selectors/store";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { formatPrice } from "@helpers/currency";
import { getRestrictionIdsString, getPricingMatrix } from "@helpers/orders/cart";
import { isByoProduct, isCollectionTypeProduct } from "@helpers/products/types";
import { getTotalFromSizeMapping } from "@helpers/product-details";

import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";

import { weglotNoTranslationClassName } from "@stadium/constants";

import { PRICING_MATRIX_PRICE_ID } from "@constants/orders/cart";
import { US_CURRENCY_CODE } from "@constants/store";

import NoImage from "@assets/images/no-image.svg";

import style from "./main.module.scss";

const emptyObj = {};

const UnavailableItemCard = ({ lineItem, removeProductFromCart, onChangeQuantity }) => {
  const {
    id,
    intent,
    images,
    name,
    restaurantName: brandName,
    price,
    restrictions,
    dietaryRestriction,
    dietaryPreference = "",
    quantity,
    optionValues,
    medleyComponents,
    sizeQtyMapping,
    minOrderableQuantity,
    masterVariant,
    isBulkAvailable,
    availableQuantity,
  } = lineItem;
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const showRestrictions = !isEmpty(restrictions) || !isEmpty(dietaryRestriction);

  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  const isOrderCurrencyUSD = orderCurrencyCode === US_CURRENCY_CODE;
  const { storeDisplayCurrencyCode } = useStoreContext();

  const storeCurrencyPrefix = useSelector(getStoreCurrencyPrefix);
  const storeCurrencySuffix = useSelector(getStoreCurrencySuffix);
  const currencyPrefix = isOrderCurrencyUSD ? "" : storeCurrencyPrefix;
  const currencySuffix = isOrderCurrencyUSD ? "" : storeCurrencySuffix;
  const spreeTaxons = useConfig(getTaxons);
  const isPricelessShop = useSelector(getIsPricelessShop);

  const hasSizeQtyMapping = Object.keys(sizeQtyMapping || emptyObj).length > 0;
  const totalPrice = hasSizeQtyMapping ? getTotalFromSizeMapping(sizeQtyMapping) : price * quantity;

  const getUpdatedPriceParams = (quantity) => {
    const productSupportsPriceMatrix = isBulkAvailable || masterVariant?.prices?.length > 1;
    if (!productSupportsPriceMatrix) return { price: lineItem.price };
    return {
      price: getPricingMatrix({ variant: masterVariant }).find(({ isInRange }) =>
        isInRange(quantity),
      )?.[PRICING_MATRIX_PRICE_ID],
    };
  };

  const renderRestrictions = (restrictionId) => {
    const restrictionData = spreeTaxons[restrictionId];
    if (!restrictionData) return null;
    const { id, name, imageUrl } = spreeTaxons[restrictionId];
    return (
      <span className={style.boxSizeItem} key={id}>
        {imageUrl.mini && <img src={imageUrl.mini || NoImage} alt="" width="16px" />}
        {name}
      </span>
    );
  };

  const renderDietaryRestriction = (restrictionKey) => {
    return (
      <span className={style.boxSizeItem} key={restrictionKey}>
        {restrictionKey}
      </span>
    );
  };

  const onChange = (value) => {
    if (isByoProduct(intent)) {
      const updatedQuantityObject = {
        medleyItemSetId: lineItem.id,
        quantity: value,
      };
      onChangeQuantity(updatedQuantityObject, intent);
    } else {
      const updatedQuantityObject = {
        productId: lineItem.productId,
        intent,
        quantity: value,
        ...getUpdatedPriceParams(value),
        storeId: lineItem.storeId,
        dietaryPreference,
        isBulkAvailable,
        medleyItemSetId: lineItem.id,
      };
      if (isCollectionTypeProduct(intent)) {
        updatedQuantityObject.orderProductSetId = id;
        updatedQuantityObject.orderProductSetRestrictionId = getRestrictionIdsString(lineItem);
      }
      onChangeQuantity(updatedQuantityObject, intent);
    }
  };

  const renderQuantity = () => {
    if (availableQuantity)
      return (
        <QuantitySelector
          minOrderableQuantity={minOrderableQuantity}
          quantity={quantity}
          setQuantity={onChange}
          decreaseBelowMinAllowed={true}
          onZeroHandler={() => removeProductFromCart(lineItem)}
          className={`defaultStyle ${weglotNoTranslationClassName}`}
        />
      );
    return (
      <InternalButton styleType="primary-outline" onClick={() => removeProductFromCart(lineItem)}>
        Remove
      </InternalButton>
    );
  };

  const getFormattedPrice = (price) => {
    if (isCustomCurrency(orderCurrencyCode)) {
      const code = isOrderCurrencyUSD ? orderCurrencyCode : storeDisplayCurrencyCode;
      return formatPrice(price, {
        prefix: currencyPrefix,
        currencyCode: code,
      });
    } else {
      return formatPriceInDisplayStoreCurrency(price, orderCurrencyCode);
    }
  };

  const renderOptionValues = ({ id, presentation, optionType }, index) => {
    const isOptionTypeColor = optionType?.key === "color";
    const parentDivClassName = isOptionTypeColor ? style.liaColorRow : "";

    return (
      <span className={`${parentDivClassName} ${style.onlyColorWithName}`}>
        <span
          className={`${style.dietryChoice} ${isOptionTypeColor ? style.colorChoice : ""}`}
          key={id}
        >
          {isOptionTypeColor ? `${optionType?.name}: ${presentation}` : `${presentation}`}
        </span>
        {!index == optionValues.length - 1 && <span className={style.choiceDivider}>|</span>}
      </span>
    );
  };

  const renderDefaultOptions = () => (
    <>
      {!isPricelessShop && (
        <div className={style.optionsBlock}>
          {[...(optionValues || [])]
            .reverse()
            .map((option, index) => renderOptionValues(option, index))}
        </div>
      )}
      {showRestrictions && (
        <div className={style.boxSizeList}>
          <span className={style.boxDetail}>BOX DETAILS:</span>{" "}
          {restrictions.map((restriction) => renderRestrictions(restriction))}
          {dietaryRestriction.map((restriction) => renderDietaryRestriction(restriction))}
        </div>
      )}
      {dietaryPreference && (
        <div className={style.preferenceWrapper}>
          <span className={style.preferenceLabel}>Preferences:</span>{" "}
          <span className={style.preferenceDetail}>{dietaryPreference}</span>
        </div>
      )}
      <div className={style.stockCount}>
        {availableQuantity ? `Only ${availableQuantity} left in stock` : "Out of stock"}
      </div>
      <div className={style.qtyParentBlock}>{renderQuantity()}</div>
    </>
  );

  return (
    <>
      <dd
        className={`cartItem ${style.cartItem} ${medleyComponents ? style["cartItemWithBYO"] : ""}`}
        key={id}
      >
        <div className={style.imageBox}>
          <Multimedia
            type="image"
            src={images?.[0] || NoImage}
            alt="Not Found"
            height={128}
            width={128}
            className={!images?.[0] ? style.imageNotAvailable : style.imageAvailable}
          />
        </div>

        <div
          className={`${style.itemDetailBox} ${
            isPricelessShop ? style["itemDetailBoxPriceless"] : ""
          }`}
        >
          <div className={style.itemBrand}>{brandName}</div>
          <div className={style.byoPill}>{isByoProduct(intent) && "BUILD-YOUR-OWN"}</div>
          <div className={style.itemName}>{name}</div>
          {renderDefaultOptions()}
        </div>
        {!isPricelessShop && (
          <div className={style.pointBlock}>
            <div className={style.pointBlockData}>
              <WeglotNoTranslationWrapper>
                {getFormattedPrice(totalPrice)}
              </WeglotNoTranslationWrapper>
              {currencySuffix && <span className={style.pointsText}>{currencySuffix}</span>}
            </div>
          </div>
        )}
      </dd>
      {showRestrictions && (
        <div className={`${style.cartItemFooter} cartFooter`}>
          <div className={style.boxSizeList}>
            <span className={style.boxDetail}>BOX DETAILS:</span>{" "}
            {restrictions.map(renderRestrictions)}
            {dietaryRestriction.map(renderDietaryRestriction)}
          </div>
        </div>
      )}
    </>
  );
};

export default UnavailableItemCard;
